<template>
  <div class="animated fadeIn campaign-data-page">
    <CRow>
      <CCol lg="12">
        <CCard class="campaign-filter-card border-0 shadow-sm">
          <CCardBody class="py-4">
            <div class="campaign-page-heading mb-4">
              <h4 class="mb-1 font-weight-bold text-dark">Campaign Data</h4>
              <p class="text-muted small mb-0">
                Choose a campaign and a date range, then load aggregated data.
              </p>
            </div>

            <CRow class="align-items-end">
              <CCol xl="4" lg="5" md="6" sm="12" class="mb-3 mb-xl-0">
                <label class="field-label">Campaign</label>
                <select
                  v-model="selectedCampaign"
                  class="form-control campaign-select"
                  :disabled="loadingCampaigns"
                >
                  <option value="">
                    {{
                      loadingCampaigns
                        ? "Loading campaigns…"
                        : "Select a campaign"
                    }}
                  </option>
                  <option
                    v-for="opt in campaignOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </CCol>

              <CCol xl="5" lg="7" md="6" sm="12" class="mb-3 mb-xl-0">
                <label class="field-label">Date range</label>
                <div class="campaign-date-wrap">
                  <VueCtkDateTimePicker
                    id="CampaignDateRange"
                    v-model="dateRange"
                    :only-date="true"
                    :range="true"
                    :no-label="true"
                    label="Select range"
                    format="YYYY-MM-DD"
                    formatted="ll"
                    color="#FF2E75"
                    :overlay="true"
                  />
                </div>
              </CCol>

              <CCol xl="3" lg="12" md="12" sm="12">
                <CButton
                  color="primary"
                  class="campaign-submit-btn w-100 font-weight-semibold"
                  :disabled="!canSubmit || loadingSubmit"
                  @click="onSubmit"
                >
                  <span v-if="!loadingSubmit">Submit</span>
                  <span v-else>Loading…</span>
                </CButton>
              </CCol>
            </CRow>

            <div
              v-if="campaignFetchError"
              class="alert alert-warning mt-3 mb-0 small"
              role="alert"
            >
              {{ campaignFetchError }}
            </div>
          </CCardBody>
        </CCard>

        <CCard
          v-if="hasSubmitted"
          class="mt-4 campaign-results-card border-0 shadow-sm"
        >
          <CCardHeader
            class="d-flex flex-wrap align-items-center justify-content-between border-0 bg-white pt-4 pb-0"
          >
            <div>
              <strong class="text-dark">Results</strong>
              <div v-if="lastQuerySummary" class="text-muted small mt-1">
                {{ lastQuerySummary }}
              </div>
            </div>
            <a-spin v-if="loadingSubmit" />
          </CCardHeader>
          <CCardBody class="pt-3">
            <div v-if="loadingSubmit" class="text-center py-5 text-muted">
              Loading data…
            </div>

            <template v-else>
              <div
                v-if="displayMode === 'error'"
                class="campaign-empty campaign-empty--error text-center py-5"
              >
                <CIcon name="cil-x-circle" size="3xl" class="text-danger mb-3" />
                <p class="mb-0 font-weight-semibold text-danger">
                  {{ submitError || "Something went wrong." }}
                </p>
              </div>

              <div
                v-else-if="displayMode === 'empty'"
                class="campaign-empty text-center py-5"
              >
                <CIcon name="cil-file" size="3xl" class="text-muted mb-3" />
                <p class="mb-0 font-weight-semibold text-muted">
                  No Data Found
                </p>
              </div>

              <div v-else-if="displayMode === 'kv'" class="campaign-kv-grid">
                <CRow>
                  <CCol
                    v-for="(val, key) in kvObject"
                    :key="key"
                    sm="12"
                    md="6"
                    lg="4"
                  >
                    <div class="campaign-kv-item">
                      <div class="campaign-kv-label">{{ humanizeKey(key) }}</div>
                      <div class="campaign-kv-value">{{ formatCell(val) }}</div>
                    </div>
                  </CCol>
                </CRow>
              </div>

              <div v-else class="campaign-table-wrap">
                <a-table
                  :columns="tableColumns"
                  :data-source="tableRows"
                  :pagination="tablePagination"
                  :scroll="{ x: 'max-content' }"
                  row-key="__rowKey"
                  size="middle"
                  class="campaign-data-table"
                />
              </div>
            </template>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function humanizeKey(key) {
  if (key == null || key === "") return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function pickFirstArray(obj) {
  if (!obj || typeof obj !== "object") return null;
  const vals = Object.values(obj);
  for (let i = 0; i < vals.length; i += 1) {
    if (Array.isArray(vals[i])) return vals[i];
  }
  return null;
}

export default {
  name: "CampaignData",
  data() {
    return {
      loadingCampaigns: false,
      loadingSubmit: false,
      campaignFetchError: "",
      submitError: "",
      campaignOptions: [],
      selectedCampaign: "",
      dateRange: {
        start: null,
        end: null,
      },
      hasSubmitted: false,
      displayMode: "empty",
      tableRows: [],
      tableColumns: [],
      kvObject: {},
      lastQuerySummary: "",
    };
  },
  computed: {
    canSubmit() {
      const dr = this.dateRange;
      return (
        !!this.selectedCampaign &&
        dr &&
        dr.start != null &&
        dr.start !== "" &&
        dr.end != null &&
        dr.end !== ""
      );
    },
    tablePagination() {
      const n = this.tableRows.length;
      if (n <= 10) return false;
      return { pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] };
    },
  },
  created() {
    this.fetchDistinctCampaigns();
  },
  methods: {
    humanizeKey,
    formatCell(val) {
      if (val === null || val === undefined) return "—";
      if (typeof val === "object") {
        try {
          return JSON.stringify(val);
        } catch (e) {
          return String(val);
        }
      }
      return val;
    },
    /**
     * Supports distinct-campaign API shapes:
     * - string[] | number[]
     * - object[]
     * - { data: [...] } | { campaigns: [...] } | { distinct_campaign: [...] } | { distinctCampaign: [...] }
     * - { data: { google: "Google", ... } } (map: slug/key -> label or nested object)
     * - { campaigns: { ... } } same as map under a key
     */
    normalizeCampaignPayload(body, depth = 0) {
      if (body == null) return [];
      if (Array.isArray(body)) return body;
      if (typeof body !== "object" || depth > 6) return [];

      const arrayKeys = [
        "data",
        "campaigns",
        "distinct_campaign",
        "distinct_campaigns",
        "distinctCampaign",
        "distinctCampaigns",
        "results",
        "items",
        "records",
        "list",
        "rows",
        "campaign_list",
        "payload",
        "response",
      ];

      for (let i = 0; i < arrayKeys.length; i += 1) {
        const k = arrayKeys[i];
        if (!Object.prototype.hasOwnProperty.call(body, k)) continue;
        const v = body[k];
        if (Array.isArray(v)) return v;
        if (v && typeof v === "object") {
          const mapped = this.mapObjectToCampaignItems(v);
          if (mapped.length) return mapped;
          const nested = this.normalizeCampaignPayload(v, depth + 1);
          if (nested.length) return nested;
        }
      }

      const fromRoot = this.mapObjectToCampaignItems(body);
      return fromRoot.length ? fromRoot : [];
    },
    /** Turn { campaignKey: labelOrObject, ... } into row objects for the dropdown. */
    mapObjectToCampaignItems(obj) {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];

      const skipKeys = new Set([
        "success",
        "message",
        "status",
        "error",
        "errors",
        "meta",
        "links",
        "pagination",
        "_meta",
      ]);

      const entries = Object.entries(obj).filter(([k]) => !skipKeys.has(k));
      if (!entries.length) return [];

      const allPrimitives = entries.every(([, v]) => {
        if (v === null || v === undefined) return true;
        const t = typeof v;
        return t === "string" || t === "number" || t === "boolean";
      });

      if (allPrimitives) {
        return entries.map(([key, val]) => ({
          campaign: key,
          display_name:
            val === null || val === undefined ? key : String(val),
        }));
      }

      const allObjects = entries.every(
        ([, v]) => v && typeof v === "object" && !Array.isArray(v)
      );
      if (allObjects) {
        return entries.map(([key, val]) => ({
          ...val,
          campaign: val.campaign ?? val.slug ?? val.code ?? key,
          display_name:
            val.display_name ??
            val.campaign_name ??
            val.label ??
            val.title ??
            val.name ??
            key,
          slug: val.slug ?? key,
        }));
      }

      return [];
    },
    campaignOptionFromItem(item) {
      if (item === null || item === undefined) return null;
      if (typeof item === "string" || typeof item === "number") {
        const v = String(item).trim();
        if (!v) return null;
        return { value: v, label: v };
      }
      if (typeof item === "object") {
        const value =
          item.value ??
          item.campaign_id ??
          item.campaign ??
          item.name ??
          item.slug ??
          item.id ??
          item.code ??
          item.key;
        if (value === undefined || value === null) return null;
        const strVal = String(value).trim();
        if (!strVal) return null;
        const label =
          item.label ??
          item.title ??
          item.display_name ??
          item.campaign_name ??
          strVal;
        return { value: strVal, label: String(label) };
      }
      return null;
    },
    fetchDistinctCampaigns() {
      this.loadingCampaigns = true;
      this.campaignFetchError = "";
      this.$axios
        .get(`${ADMIN_URL}/distinct-campaign`)
        .then((res) => {
          const list = this.normalizeCampaignPayload(res.data);
          const opts = [];
          const seen = new Set();
          list.forEach((item) => {
            const o = this.campaignOptionFromItem(item);
            if (o && !seen.has(o.value)) {
              seen.add(o.value);
              opts.push(o);
            }
          });
          this.campaignOptions = opts;
        })
        .catch((err) => {
          const msg =
            (err &&
              err.data &&
              (err.data.message || err.data.error)) ||
            (err && err.message) ||
            "Could not load campaigns.";
          this.campaignFetchError = msg;
          this.campaignOptions = [];
        })
        .finally(() => {
          this.loadingCampaigns = false;
        });
    },
    formatDateParam(d) {
      if (d == null || d === "") return "";
      if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d)) {
        return d.slice(0, 10);
      }
      const str = String(d);
      return str.length >= 10 ? str.slice(0, 10) : str;
    },
    normalizeCampaignDataBody(body) {
      if (body == null) return { mode: "empty", rows: [], kv: null };
      if (Array.isArray(body)) {
        return { mode: "table", rows: body, kv: null };
      }
      if (typeof body === "object") {
        if (Array.isArray(body.data)) {
          return { mode: "table", rows: body.data, kv: null };
        }
        if (Array.isArray(body.rows)) {
          return { mode: "table", rows: body.rows, kv: null };
        }
        if (Array.isArray(body.result)) {
          return { mode: "table", rows: body.result, kv: null };
        }
        if (Array.isArray(body.items)) {
          return { mode: "table", rows: body.items, kv: null };
        }
        const nested = pickFirstArray(body);
        if (nested) {
          return { mode: "table", rows: nested, kv: null };
        }
        const primitiveOnly = Object.values(body).every(
          (v) =>
            v === null ||
            v === undefined ||
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean"
        );
        if (primitiveOnly && Object.keys(body).length) {
          return { mode: "kv", rows: [], kv: { ...body } };
        }
        return { mode: "kv", rows: [], kv: { ...body } };
      }
      return { mode: "empty", rows: [], kv: null };
    },
    buildTableModel(rows) {
      if (!rows.length) {
        return { columns: [], data: [] };
      }
      const first = rows[0];
      if (first !== null && typeof first === "object" && !Array.isArray(first)) {
        const keys = Object.keys(first);
        const columns = keys.map((key) => ({
          title: humanizeKey(key),
          dataIndex: key,
          key,
          ellipsis: true,
          customRender: (text) => this.formatCell(text),
        }));
        const data = rows.map((row, idx) => {
          const base =
            row && typeof row === "object" && !Array.isArray(row)
              ? { ...row }
              : { value: row };
          return {
            ...base,
            __rowKey: base.id != null ? String(base.id) : `row-${idx}`,
          };
        });
        return { columns, data };
      }
      const data = rows.map((row, idx) => ({
        value: row,
        __rowKey: `row-${idx}`,
      }));
      return {
        columns: [
          {
            title: "Value",
            dataIndex: "value",
            key: "value",
            ellipsis: true,
            customRender: (text) => this.formatCell(text),
          },
        ],
        data,
      };
    },
    onSubmit() {
      if (!this.canSubmit) return;
      this.submitError = "";
      this.loadingSubmit = true;
      this.hasSubmitted = true;

      const startDate = this.formatDateParam(this.dateRange.start);
      const endDate = this.formatDateParam(this.dateRange.end);
      this.lastQuerySummary = `Campaign: ${this.selectedCampaign} · ${startDate} → ${endDate}`;

      this.$axios
        .get(`${ADMIN_URL}/campaign-data`, {
          params: {
            campaign: this.selectedCampaign,
            startDate,
            endDate,
          },
        })
        .then((res) => {
          const normalized = this.normalizeCampaignDataBody(res.data);
          if (normalized.mode === "kv" && normalized.kv) {
            this.displayMode = "kv";
            this.kvObject = normalized.kv;
            this.tableRows = [];
            this.tableColumns = [];
            return;
          }
          const rows = Array.isArray(normalized.rows) ? normalized.rows : [];
          if (!rows.length) {
            this.displayMode = "empty";
            this.tableRows = [];
            this.tableColumns = [];
            this.kvObject = {};
            return;
          }
          const { columns, data } = this.buildTableModel(rows);
          this.displayMode = "table";
          this.tableColumns = columns;
          this.tableRows = data;
          this.kvObject = {};
        })
        .catch((err) => {
          this.tableRows = [];
          this.tableColumns = [];
          this.kvObject = {};
          const msg =
            (err &&
              err.data &&
              (err.data.message || err.data.error)) ||
            (err && err.message) ||
            "Request failed.";
          this.submitError = msg;
          this.displayMode = "error";
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    },
  },
};
</script>

<style scoped>
.campaign-data-page {
  max-width: 1200px;
  margin: 0 auto;
}

.campaign-filter-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.campaign-results-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.field-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.35rem;
}

.campaign-select {
  border-radius: 8px;
  min-height: 42px;
  border-color: #dee2e6;
}

.campaign-select:focus {
  border-color: #ff2e75;
  box-shadow: 0 0 0 0.15rem rgba(255, 46, 117, 0.15);
}

.campaign-date-wrap {
  border-radius: 8px;
}

.campaign-submit-btn {
  min-height: 42px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff2e75 0%, #e02666 100%);
  border: none;
}

.campaign-submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.campaign-empty {
  border: 1px dashed #dee2e6;
  border-radius: 12px;
  background: #fafbfc;
}

.campaign-kv-item {
  border: 1px solid #eef0f2;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
  background: #fff;
  transition: box-shadow 0.15s ease;
}

.campaign-kv-item:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.campaign-kv-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8b95a1;
  margin-bottom: 0.35rem;
}

.campaign-kv-value {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  word-break: break-word;
}

.campaign-table-wrap {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eef0f2;
}
</style>

<style>
/* Ant table header polish (non-scoped: ant renders in subtree) */
.campaign-data-page .ant-table-thead > tr > th {
  background: #f8f9fb !important;
  font-weight: 600;
  color: #495057 !important;
  border-bottom: 2px solid #e9ecef !important;
}

.campaign-data-page .ant-table-tbody > tr:hover > td {
  background: #fff5f8 !important;
}
</style>
